import { useMemo, useState } from 'react'

import { useLocales } from 'src/contexts'

import { useManageServiceContacts } from 'src/hooks/useManageServiceContacts'
import { useInputRefForFocus } from 'src/hooks/useInputRefFocus'
import { useDebounceState } from 'src/hooks/useDebounceState'

import { SortStates } from 'src/pages/ServiceContactsHistory/types'
import {
  getListOfSortsStates,
  handleSortChange,
} from 'src/pages/ServiceContactsHistory/helpers'

export const useContactsHistory = () => {
  const { translations } = useLocales()

  const { inputRef } = useInputRefForFocus()

  const breadcrumbItems = [
    { labelKey: translations.breadcrumbItems.home, href: '/' },
    {
      labelKey: translations.breadcrumbItems.serviceContacts,
      href: '/service/history',
    },
    { labelKey: translations.breadcrumbItems.history },
  ]

  const { allServiceContacts, isLoadingGetContacts } =
    useManageServiceContacts()

  const [sortState, setSortState] = useState<SortStates>(SortStates.BY_NEW)

  const [
    searchText,
    submitSearchText,
    handleSearchChange,
    setCurrentDebouncedValue,
    setCurrentValue,
  ] = useDebounceState('')

  const filteredHistory = useMemo(() => {
    if (!allServiceContacts) return []

    return allServiceContacts.filter(item =>
      JSON.stringify(item)
        .toLowerCase()
        .includes(submitSearchText.toLowerCase()),
    )
  }, [allServiceContacts, submitSearchText])

  const sortedHistory = useMemo(() => {
    if (sortState === SortStates.BY_NEW) {
      return filteredHistory
    }

    const baseArray = [...filteredHistory]

    return baseArray.sort(handleSortChange(sortState))
  }, [filteredHistory, sortState])

  const handleChangeSortState = (value: SortStates) => setSortState(value)

  const listOfSorts = useMemo(() => {
    return getListOfSortsStates(
      translations.serviceContacts.historyServiceContacts,
    )
  }, [translations])

  const clearInput = (): void => {
    setCurrentDebouncedValue('')
    setCurrentValue('')
  }

  return {
    locale: translations.serviceContacts.historyServiceContacts,
    listOfSorts,
    allContactsHistory: sortedHistory,
    isLoadingGetContacts,
    sortState,
    handleChangeSortState,
    breadcrumbItems,
    searchText,
    handleSearchChange,
    clearInput,
    inputRef,
    numberOfFoundItems: sortedHistory.length,
  }
}
